import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button, LinkButton } from "../../../../components/Button";
import Input from "../../../../components/Form/Input/input";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { FreeKeeposLimitModal } from "../../../../components/FreeKeeposLimitModal";
import { KeepoLoader } from "../../../../components/KeepoLoader";
import { Container as BarContainer, Box as BarBox, StickySpacer } from "../../../../components/SaveBar/styles";
import { ScreenHeader } from "../../../../components/SubHeader";
import constants from "../../../../constants";
import { useAppState } from "../../../../contexts/App";
import { usePreviewSite } from "../../../../contexts/PreviewSite";
import { useSite } from "../../../../contexts/Site";
import { useCheckUrl } from "../../../../hooks/CheckUrl";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import { getPages } from "../../../../model/pages";
import { api, hasActiveSubscription } from "../../../../services/api";
import { ERR_FREE_SITES_LIMIT_REACHED, isApiError } from "../../../../services/api/errors";
import { isPlusRequiredCreation } from "../../../../utils/plusRequiredCreation";
import { takeUrlSuggestion } from "../../../../utils/urlSuggestion";
import CreationProgress from "../../components/CreationProgress";
import { Card, Container, Section } from "../Title/styles";
import { AddressStatus } from "./styles";

/** How long the screen waits for the suggested address before leaving the field empty (ms). */
export const URL_SUGGESTION_WAIT_MS = 8000;

const PublishPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();
  const queryUrl = useQueryUrl();

  const { url, onChangeUrl, urlReady, loadingUrl, urlNotAvailable } = useCheckUrl(
    appState?.editingSiteId as string,
    site?.url || ""
  );

  // A Keepo without an address yet comes with one suggested from its name (asked when the name was saved): the
  // person goes on with it or changes it. What the person types first wins over a suggestion that comes late.
  const [suggesting, setSuggesting] = useState(!site?.url && !appState?.published);
  const typed = useRef(false);
  useEffect(() => {
    if (!suggesting) return undefined;
    let alive = true;
    let wait: ReturnType<typeof setTimeout> | undefined;
    const waited = new Promise<null>(resolve => {
      wait = setTimeout(() => resolve(null), URL_SUGGESTION_WAIT_MS);
    });
    Promise.race([takeUrlSuggestion(appState.editingSiteId as string), waited]).then(suggested => {
      clearTimeout(wait);
      if (!alive) return;
      setSuggesting(false);
      if (suggested && !typed.current) onChangeUrl(suggested);
    });
    return () => {
      alive = false;
      clearTimeout(wait);
    };
    // once, when the screen opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The preview shows the address being typed, as in a browser's address bar
  const { setPreviewAddress } = usePreviewSite();
  useEffect(() => {
    setPreviewAddress(url ? `${url}.${constants.nameSite}` : undefined);
  }, [url, setPreviewAddress]);
  useEffect(() => () => setPreviewAddress(undefined), [setPreviewAddress]);

  const [isPublishing, setPublishing] = useState(false);
  const [showFreeKeeposLimitModal, setShowFreeKeeposLimitModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const publishHandler = async () => {
    setPublishing(true);
    setErrorMessage(undefined);

    const copy = { ...site, url };
    const shouldPublishNow =
      appState?.published ||
      hasActiveSubscription(appState.editingSiteSubscription) ||
      // with pages it always goes by the PLUS page, which tells that they stay off without PLUS
      (queryUrl.get("skipPlusOffer") === "1" && !getPages(site).length);

    // The address is kept in the draft; publishing is the next step's (after the PLUS page for FREE)
    try {
      await api.saveDraft(appState.editingSiteId as string, copy);
      setSite(copy);
      setAppState({ ...appState, editingSiteModified: true });
      navigate(
        shouldPublishNow
          ? "/editor/publish/ready"
          : `/subscription/features?site=${appState.editingSiteId}&flow=create${
              isPlusRequiredCreation(appState.editingSiteId as string) ? "&plusRequired=1" : ""
            }`
      );
    } catch (e) {
      if (isApiError(e, ERR_FREE_SITES_LIMIT_REACHED)) {
        setShowFreeKeeposLimitModal(true);
      } else {
        setErrorMessage(t("pages.editor.publish.errors.generic"));
      }
      setPublishing(false);
    }
  };

  const continueCreatingPlus = () => {
    setShowFreeKeeposLimitModal(false);
    navigate(`/subscription/features?site=${appState.editingSiteId}`);
  };

  return (
    <Container>
      {showFreeKeeposLimitModal && (
        <FreeKeeposLimitModal
          i18nKeyPrefix="pages.editor.publish.freeKeeposLimitReached"
          onConfirm={continueCreatingPlus}
          onCancel={() => setShowFreeKeeposLimitModal(false)}
        />
      )}
      {/* where the person is in the flow, on top; the screen's own header under it */}
      <CreationProgress step="address" />
      <ScreenHeader
        title={t("pages.editor.publish.header.title")}
        back={{ label: t("pages.editor.templates.back"), to: "/editor/style?flow=publish" }}
      />
      <Form onSubmit={e => e.preventDefault()}>
        <Section>
          <Card>
            <FormGroup>
              <Input
                type="text"
                id="url"
                ignoreLengthInfo
                disabled={appState?.published}
                autoCapitalize="none"
                value={url}
                maxLength={30}
                autoFocus={!url}
                sufix={`.${constants.nameSite}`}
                onChange={e => {
                  typed.current = true;
                  onChangeUrl(e.target.value.replace(/\s/g, ""));
                }}
                label={t("pages.editor.publish.form.url")}
              />
            </FormGroup>
            {/* While the address is suggested or checked, the line under the field says so */}
            {suggesting && !url ? (
              <AddressStatus className="checking" role="status">
                <KeepoLoader size={9} />
                {t("pages.editor.publish.form.suggesting")}
              </AddressStatus>
            ) : url && loadingUrl ? (
              <AddressStatus className="checking" role="status">
                <KeepoLoader size={9} />
                {t("pages.editor.publish.form.checking")}
              </AddressStatus>
            ) : url && (urlNotAvailable || (urlReady && !appState?.published)) ? (
              <AddressStatus $available={urlReady}>
                {urlNotAvailable
                  ? t("pages.editor.publish.form.urlNotAvailable.message")
                  : t("pages.editor.publish.form.urlAvailable.message")}
              </AddressStatus>
            ) : (
              <AddressStatus>{t("pages.editor.publish.form.hint")}</AddressStatus>
            )}
            {errorMessage && <p className="error">{errorMessage}</p>}
          </Card>
        </Section>
      </Form>

      {/* Same fixed bar as the other steps */}
      <StickySpacer />
      <BarBox $sticky>
        <BarContainer>
          <LinkButton onClick={() => navigate("/editor/style?flow=publish")}>
            {t("pages.editor.templates.back")}
          </LinkButton>
          <Button
            type="INFO"
            size="LG"
            disabled={!urlReady || isPublishing}
            $loading={isPublishing}
            onClick={() => urlReady && !isPublishing && publishHandler()}>
            {t("pages.editor.preview.footer.actions.continue")}
          </Button>
        </BarContainer>
      </BarBox>
    </Container>
  );
};

export default PublishPage;
